<template>
    <ion-grid
        class="card-border"
        :class="{
            'px-0': isReducedList,
            'pb-0': isReducedList,
            'coral-coral-background p-2 input-amount-container': !isReducedList,
            slim
        }"
    >
        <ion-row @click.stop @click.prevent style="flex-wrap: nowrap;" class="d-flex ion-justify-content-between">
            <ion-col
                class="d-flex ion-justify-content-end"
                :class="{ disabled, 'px-0': isReducedList }"
                :size="isReducedList ? '4' : undefined"
            >
                <button-minus
                    class="round-button tint-button amount-button"
                    :class="{ disabled, slim, 'outline-button': isReducedList }"
                    :color="isReducedList ? ION_VARIANT.LIGHT : ION_VARIANT.CORAL"
                    @click="changeValue(-1 * (amountStep ?? 1), $event)"
                    @mousedown="quickChange(-1 * (amountStep ?? 1))"
                    @mouseup="stopQuickChange"
                    @touchstart.passive="quickChange(-1 * (amountStep ?? 1))"
                    @touchend.passive="stopQuickChange"
                    @contextmenu="$event.stopPropagation()"
                    :disabled="disabled"
                />
            </ion-col>

            <ion-col :size="isReducedList ? '4' : 'auto'" class="input-col center-y" :class="{center: isReducedList}">
                <input
                    class="light-light-background max-size center ion-text-center remove-padding-input amount-input amount-input-fix"
                    :class="{
                        small,
                        slim,
                        reduced: isReducedList,
                        'grey4-grey4-background': isReducedList,
                        'purple-purple-color': isReducedList,
                        'coral-coral-color': !isReducedList
                    }"
                    @click.stop="selectValue($event)"
                    v-model.number="inputValue"
                    :min="props.minValue"
                    id="input-amount-cart"
                    ref="amountInput"
                    :step="String(amountStep)"
                    inputmode="numeric"
                    type="text"
                    :disabled="directInputDisabled || (disabled ?? false)"
                    @focusin="handleFocus">
            </ion-col>

            <ion-col
                :class="{ disabled, 'px-0': isReducedList }"
                :size="isReducedList ? '4' : undefined"
            >
                <button-plus
                    class="round-button tint-button amount-button"
                    :class="{ disabled, slim, 'outline-button': isReducedList }"
                    :color="isReducedList ? ION_VARIANT.LIGHT : ION_VARIANT.CORAL"
                    @click="changeValue(amountStep ?? 1, $event)"
                    @mousedown="quickChange(amountStep ?? 1)"
                    @mouseup="stopQuickChange"
                    @touchstart.passive="quickChange(amountStep ?? 1)"
                    @touchend.passive="stopQuickChange"
                    @contextmenu="$event.stopPropagation()"
                    :disabled="disabled"
                />
            </ion-col>
        </ion-row>
    </ion-grid>
</template>

<script setup lang="ts">
import { InputCustomEvent, IonCol, IonGrid, IonInput, IonRow, } from "@ionic/vue";
import { computed, ref, onMounted, onBeforeUnmount } from "vue";

import ButtonMinus from "@/components/button/ButtonMinus.vue";
import ButtonPlus from "@/components/button/ButtonPlus.vue";

import { MIN_VALUE, INITIAL_TIMER, TIMER_SUBSTRACTION, TIMER_MIN } from "@/assets/variables";

import { ION_VARIANT } from "@/types/enum/ionicEnum";
import { InputAmountProps } from "@/types/props";
import { InputAmountEmits } from "@/types/emits";

const props = withDefaults(defineProps<InputAmountProps>(), {
    minValue: 0
})
const emit = defineEmits<InputAmountEmits>()

const amountInput = ref()
const changeTimer = ref<number>(INITIAL_TIMER)
const changeable = ref<boolean>(true)
const counterTimeout = ref<number | undefined>(0)

let preventBlur = false

const inputValue = computed<string | number>({
    get: () => {
        if (!props.modelValue) return 0
        return props.modelValue
    },
    set: async (value) => {
        // const convertedNumber = Number(value)
        const convertedNumber = Number(String(value).replace(/[^0-9]*/g, ''));

        if (isNaN(convertedNumber) || value === '' || value === '00') {
            emit('update:modelValue', props.minValue)
            amountInput.value.value = props.minValue;
            changeable.value = true
            return
        }

        if (convertedNumber < props.minValue) {
            emit('update:modelValue', props.minValue)
            amountInput.value.value = props.minValue;
            changeable.value = true
            return
        }

        if (props.max && convertedNumber > props.max) {
            emit('update:modelValue', props.max)
            amountInput.value.value = props.max;
            changeable.value = true
            return
        }

        if (!changeable.value) return
        changeable.value = false

        emit('update:modelValue', convertedNumber)
        emit('change', convertedNumber)
        amountInput.value.value = convertedNumber;

        changeable.value = true
    },
})

const handleFocus = (event: FocusEvent) => {
    if (preventBlur) {
        preventBlur = false
        return
    }
    const target = event.target as HTMLInputElement;
    target?.blur()
}

const select = (event: InputCustomEvent): void => {
    if (props.preventPropagation && event?.type === 'click') event.stopPropagation()
    if (event.target.select) { // tapped directly on html input
        preventBlur = true
        event.target.select();
    } else if (event.target.children[0].select) { // tapped on the wrapper
        preventBlur = true
        event.target.children[0].select();
    }
}

const selectValue = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    preventBlur = true
    target.select();
}

const changeValue = (modifier: number, event?: MouseEvent): void => {
    if (props.preventPropagation && event?.type === "click") event.stopPropagation()
    if (!changeable.value) return
    changeable.value = false

    if (!props.modelValue) {
        if (props.modelValue === props.minValue) changeable.value = true
        emit('update:modelValue', modifier < 0 ? props.minValue : modifier)
        return
    }

    if ((Number(props.modelValue) + modifier < props.minValue && modifier < 0) || (props.max && (Number(props.modelValue) + modifier > props.max))) {
        changeable.value = true
        return
    }

    emit('update:modelValue', Number(props.modelValue) + modifier)
    changeable.value = true
}

const nextChangeTick = (modifier: number): void => {
    if (!changeable.value) return
    changeable.value = false

    counterTimeout.value = setTimeout(() => {
        changeable.value = true
        changeValue(modifier)
        if (changeTimer.value - TIMER_SUBSTRACTION >= TIMER_MIN)
            changeTimer.value -= TIMER_SUBSTRACTION
        nextChangeTick(modifier)
    }, changeTimer.value)
}

const quickChange = (modifier: number): void => {
    if (counterTimeout.value) return
    nextChangeTick(modifier)
}

const stopQuickChange = (): void => {
    clearTimeout(counterTimeout.value)
    changeable.value = true
    counterTimeout.value = undefined
    changeTimer.value = INITIAL_TIMER
}

onMounted(() => {
  document.addEventListener('mouseup', stopQuickChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('mouseup', stopQuickChange)
})
</script>

<style scoped>
.amount-button {
    width: 45px !important;
    height: 45px !important;
    min-height: 45px !important;
    min-width: 45px !important;
    --border-radius: 50%;
    border-radius: 50%;
}

.amount-input {
    width: 12ch;
    border-radius: 16px;
    --border-radius: 16px;
    --placeholder-color: var(--ion-color-grey1);
    --highlight-color-focused: none;
    min-height: 38px;
    max-width: 60px;
    max-height: 38px;
}

.amount-input:disabled {
    color: var(--ion-color-grey1);
    cursor: not-allowed;
    opacity: unset;
}

.amount-input.small {
    width: 7ch;
}

.reduced.small {
    width: 4ch;
    border-radius: 10px;
    --border-radius: 10px;
}

.amount-button.slim {
    height: 30px !important;
    width: 30px !important;
    min-height: 30px !important;
    min-width: 30px !important;
}

.amount-input.slim {
    min-height: 30px;
}

.input-amount-container.slim {
    min-width: 180px;
    max-width: 180px;
}

.input-amount-container.slim > ion-row > ion-col:not(.input-col) {
    max-width: 40px;
}

.amount-input-fix {
    border: none;
    outline: none;
    font-size: 1rem;
    width: 60px;
    max-height: 44px;
    color: var(--ion-color-coral);
}

@media (max-width: 395px) {
    .amount-button {
        width: 35px !important;
        height: 35px !important;
        min-height: 35px !important;
        min-width: 35px !important;
    }

    .amount-input {
        width: 10ch;
    }
}
</style>
